'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from '@to/ui';

interface AgentSearchBarProps {
  /** The committed search query (owned by the page). */
  value: string;
  /** Fired 300ms after typing stops, or immediately when cleared. */
  onChange: (q: string) => void;
  placeholder?: string;
  busy?: boolean;
}

/**
 * Controlled, debounced search input for the Find Your Agent surface (Phase 31).
 *
 * Holds a local draft seeded from `value` so keystrokes feel instant, but only
 * commits upstream 300ms after the user stops typing. Stays in sync if `value`
 * changes externally (including Back/Forward), but a completed search never
 * replaces a newer draft. The clear button commits `""`
 * immediately and refocuses the field. Every color via `--brand-*`; icons via
 * `Icon` (Material Symbols Rounded: `search` / `close`).
 */
export function AgentSearchBar({
  value,
  onChange,
  placeholder,
  busy = false,
}: AgentSearchBarProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(value);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitted = useRef<string[]>([]);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Server results acknowledge a submitted query; they do not own the draft.
  // Next discards superseded navigations, so an acknowledgement also retires
  // earlier submissions. Other value changes are deliberate external resets.
  useEffect(() => {
    const index = submitted.current.indexOf(value);
    if (index !== -1) {
      submitted.current.splice(0, index + 1);
      return;
    }
    submitted.current = [];
    if (timer.current) clearTimeout(timer.current);
    // The URL is external state; only deliberate navigation replaces the draft.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(value);
  }, [value]);

  useEffect(() => {
    // A history entry may have the same query as an outstanding search. Treat
    // Back/Forward as explicit intent even before its server props arrive.
    const restoreHistory = () => {
      if (timer.current) clearTimeout(timer.current);
      submitted.current = [];
      setDraft(new URLSearchParams(window.location.search).get('q') ?? '');
    };
    window.addEventListener('popstate', restoreHistory);
    return () => {
      window.removeEventListener('popstate', restoreHistory);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  function commit(query: string) {
    // URL parsing trims queries before passing them back with server results.
    submitted.current.push(query.trim());
    onChangeRef.current(query);
  }

  function edit(query: string) {
    setDraft(query);
    if (timer.current) clearTimeout(timer.current);
    // Schedule from the keystroke, not from result renders or callback changes.
    timer.current = setTimeout(() => commit(query), 300);
  }

  return (
    <div className="relative w-full">
      <label htmlFor={inputId} className="sr-only">
        Search agents
      </label>
      <span
        className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4"
        style={{ color: 'var(--brand-input-placeholder)' }}
      >
        <Icon name="search" size={20} />
      </span>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        value={draft}
        aria-busy={busy}
        onChange={(e) => edit(e.target.value)}
        placeholder={placeholder ?? 'Search by name, brokerage, or location...'}
        className="w-full rounded-xl border py-3.5 pl-12 pr-11 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
        style={{
          backgroundColor: 'var(--brand-card-bg)',
          borderColor: 'var(--brand-input-border)',
          color: 'var(--brand-text)',
          boxShadow: 'var(--shadow-resting)',
        }}
      />
      {draft && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            if (timer.current) clearTimeout(timer.current);
            setDraft('');
            commit('');
            inputRef.current?.focus();
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-4 transition-colors"
          style={{ color: 'var(--brand-input-placeholder)' }}
        >
          <Icon name="close" size={18} />
        </button>
      )}
    </div>
  );
}
